<script setup lang="ts">
/**
 * The application frame.
 *
 * Anatomy, taken from the reference kit: the sidebar sits bare on the page
 * background with no divider, and everything else lives inside a single
 * rounded card that floats on that background with a small gutter around it.
 * The top bar belongs to that card, not to the window. That gutter is the
 * whole trick — it is what stops the interface reading as a plain web page.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import {
    BarChart3,
    BookOpen,
    CalendarDays,
    ChevronsUpDown,
    LogOut,
    Moon,
    PanelLeft,
    Search,
    Settings,
    Sun,
    Target,
    Trophy,
    User as UserIcon
} from 'lucide-vue-next'
import { useAuth } from '@/composables/useAuth'
import { useCurrentRound } from '@/composables/useCurrentRound'
import { useSeason } from '@/composables/useSeason'
import { useTheme } from '@/composables/useTheme'
import { matchdayState } from '@/lib/format'

const route = useRoute()
const router = useRouter()
const { user, isAdmin, logout } = useAuth()
const { season } = useSeason()
const { resolvedTheme, setTheme } = useTheme()
const round = useCurrentRound()

const sidebarExpanded = ref(true)
const searchInput = ref<HTMLInputElement | null>(null)
const query = ref('')
const userMenuOpen = ref(false)

const isDark = computed(() => resolvedTheme.value === 'dark')

// `short` is the bottom-bar label: a phone tab has room for one word.
const gameNav = [
    { label: 'Jugar', short: 'Jugar', to: '/', icon: Target },
    { label: 'Jornadas', short: 'Jornadas', to: '/matchdays', icon: CalendarDays },
    { label: 'Ranking', short: 'Ranking', to: '/ranking', icon: Trophy },
    { label: 'Mis estadísticas', short: 'Stats', to: '/statistics', icon: BarChart3 },
    { label: 'Reglas', short: 'Reglas', to: '/rules', icon: BookOpen }
]

const adminNav = [{ label: 'Panel de admin', to: '/admin', icon: Settings }]

const initials = computed(() => {
    const name = user.value?.name || user.value?.email || '?'
    return name
        .split(/\s+/)
        .map((part) => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
})

/**
 * The sidebar card. It states where the open round stands and what is left to
 * do — never a number it does not have, so an unloaded round says so plainly.
 */
const current = computed(() => {
    const matchday = round.matchday.value
    if (!matchday) {
        return {
            number: null as number | null,
            note: round.loading.value
                ? 'Cargando la jornada en curso…'
                : 'No hay ninguna jornada abierta ahora mismo.',
            action: 'Ir a jugar',
            dot: 'bg-muted-foreground'
        }
    }

    const state = matchdayState(matchday.status)
    const left = round.total.value - round.filled.value

    if (state === 'processed') {
        return {
            number: matchday.matchdayNumber,
            note: `Jornada puntuada. ${round.prediction.value?.totalPoints ?? 0} puntos y ${round.prediction.value?.totalGoals ?? 0} goles en esta ronda.`,
            action: 'Ver resultados',
            dot: 'bg-status-processed'
        }
    }

    if (state === 'locked') {
        return {
            number: matchday.matchdayNumber,
            note: 'Los pronósticos están cerrados. Quedan por jugarse los partidos.',
            action: 'Ver la jornada',
            dot: 'bg-status-locked'
        }
    }

    return {
        number: matchday.matchdayNumber,
        note:
            left > 0
                ? `Te faltan ${left} de ${round.total.value} pronósticos por rellenar.`
                : `Tu quiniela está sellada: ${round.total.value} de ${round.total.value}.`,
        action: left > 0 ? 'Completar quiniela' : 'Revisar quiniela',
        dot: left > 0 ? 'bg-status-pending' : 'bg-status-processed'
    }
})

function toggleTheme() {
    setTheme(isDark.value ? 'light' : 'dark')
}

function onKeydown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        searchInput.value?.focus()
    }
}

function submitSearch() {
    const q = query.value.trim()
    if (!q) return
    // The ranking is the only searchable list, so that is where a query goes.
    void router.push({ path: '/ranking', query: { q } })
}

/** The native ✕ fires input, not submit: an emptied box must undo its query. */
function onSearchInput() {
    if (query.value === '' && route.path === '/ranking' && route.query.q) {
        void router.replace({ path: '/ranking' })
    }
}

async function onLogout() {
    userMenuOpen.value = false
    await logout()
    void router.replace({ name: 'TheLogin' })
}

onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    void round.load()
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
    <div class="flex min-h-dvh bg-background">
        <!-- Sidebar: no border, no panel. It reads as part of the page.
             Desktop only — phones navigate with the bottom bar instead. -->
        <aside
            :class="[
                'sticky top-0 hidden h-dvh shrink-0 flex-col transition-[width] duration-200 lg:flex',
                sidebarExpanded ? 'w-64' : 'w-16'
            ]"
        >
            <RouterLink
                to="/"
                class="flex h-16 shrink-0 items-center justify-center px-4 lg:justify-start"
                aria-label="Quiniela"
            >
                <span
                    class="grid size-7 shrink-0 place-items-center rounded-[7px] bg-foreground text-[11px] font-bold text-background"
                >
                    Q
                </span>
                <span
                    v-if="sidebarExpanded"
                    class="ml-3 hidden truncate text-sm font-semibold tracking-[-0.02em] lg:inline"
                >
                    Quiniela
                </span>
                <ChevronsUpDown
                    v-if="sidebarExpanded"
                    class="ml-auto hidden size-3.5 text-muted-foreground lg:block"
                />
            </RouterLink>

            <nav class="min-h-0 flex-1 overflow-y-auto scrollbar-none px-2 py-2 lg:px-4">
                <p
                    v-if="sidebarExpanded"
                    class="mb-1.5 hidden px-2 text-[12px] font-medium text-muted-foreground lg:block"
                >
                    Juego
                </p>
                <RouterLink
                    v-for="item in gameNav"
                    :key="item.to"
                    :to="item.to"
                    :title="item.label"
                    class="mb-0.5 flex h-9 items-center justify-center rounded-md text-[14px] text-muted-foreground transition-colors hover:bg-secondary/70 hover:text-foreground lg:justify-start lg:px-2"
                    exact-active-class="!bg-secondary !font-medium !text-foreground"
                    :active-class="item.to === '/' ? '' : '!bg-secondary !font-medium !text-foreground'"
                >
                    <component :is="item.icon" class="size-4 shrink-0" :stroke-width="1.8" />
                    <span v-if="sidebarExpanded" class="ml-2.5 hidden truncate lg:inline">
                        {{ item.label }}
                    </span>
                </RouterLink>

                <template v-if="isAdmin">
                    <p
                        v-if="sidebarExpanded"
                        class="mb-1.5 mt-7 hidden px-2 text-[12px] font-medium text-muted-foreground lg:block"
                    >
                        Admin
                    </p>
                    <RouterLink
                        v-for="item in adminNav"
                        :key="item.to"
                        :to="item.to"
                        :title="item.label"
                        class="mb-0.5 flex h-9 items-center justify-center rounded-md text-[14px] text-muted-foreground transition-colors hover:bg-secondary/70 hover:text-foreground lg:justify-start lg:px-2"
                        active-class="!bg-secondary !font-medium !text-foreground"
                    >
                        <component :is="item.icon" class="size-4 shrink-0" :stroke-width="1.8" />
                        <span v-if="sidebarExpanded" class="ml-2.5 hidden truncate lg:inline">
                            {{ item.label }}
                        </span>
                    </RouterLink>
                </template>
            </nav>

            <div v-if="sidebarExpanded" class="relative hidden shrink-0 px-4 pb-5 lg:block">
                <!-- Where the round stands, without leaving the sidebar. On
                     Jugar the sheet itself is on screen: two save prompts with
                     two different states on one screen made a tester abandon
                     his save, so the card yields there. -->
                <div
                    v-if="route.path !== '/' && route.path !== '/matchdays'"
                    class="mb-3 flex min-h-[13rem] flex-col rounded-xl border border-border bg-card p-3 shadow-(--shadow-card)"
                >
                    <p class="text-sm font-semibold tracking-tight">Jornada {{ current.number ?? '—' }}</p>
                    <p class="mt-1 text-xs leading-5 text-muted-foreground">
                        {{ current.note }}
                    </p>

                    <!-- How much of the sheet is done, without leaving the bar. -->
                    <div v-if="round.total.value" class="mt-4">
                        <p class="flex items-baseline justify-between text-[11px] text-muted-foreground">
                            <span>Pronósticos</span>
                            <span class="tabular-nums">
                                {{ round.filled.value }}/{{ round.total.value }}
                            </span>
                        </p>
                        <span class="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-secondary">
                            <span
                                class="block h-full rounded-full bg-foreground transition-[width] duration-300"
                                :style="{
                                    width: `${(round.filled.value / round.total.value) * 100}%`
                                }"
                            />
                        </span>
                    </div>

                    <RouterLink
                        to="/"
                        class="mt-auto flex h-9 items-center justify-center gap-2 rounded-md bg-foreground text-xs font-medium text-background"
                    >
                        <span :class="['size-2 rounded-full', current.dot]" />
                        {{ current.action }}
                    </RouterLink>
                </div>

                <button
                    type="button"
                    class="flex h-12 w-full items-center gap-2.5 rounded-md px-1 text-left hover:bg-secondary/60"
                    @click="userMenuOpen = !userMenuOpen"
                >
                    <span
                        class="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-[12px] font-medium"
                    >
                        {{ initials }}
                    </span>
                    <span class="min-w-0 flex-1">
                        <span class="block truncate text-xs font-medium">
                            {{ user?.name || 'Jugador' }}
                        </span>
                        <span class="block truncate text-[12px] text-muted-foreground">
                            {{ user?.email }}
                        </span>
                    </span>
                    <ChevronsUpDown class="size-4 shrink-0 text-muted-foreground" />
                </button>

                <template v-if="userMenuOpen">
                    <div class="fixed inset-0 z-40" aria-hidden="true" @click="userMenuOpen = false" />
                    <div
                        class="absolute bottom-16 left-4 right-4 z-50 rounded-md border border-border bg-popover p-1 text-sm shadow-(--shadow-overlay)"
                    >
                        <RouterLink
                            to="/profile"
                            class="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-secondary"
                            @click="userMenuOpen = false"
                        >
                            <UserIcon class="size-4" />
                            Mi perfil
                        </RouterLink>
                        <button
                            type="button"
                            class="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-secondary"
                            @click="onLogout"
                        >
                            <LogOut class="size-4" />
                            Cerrar sesión
                        </button>
                    </div>
                </template>
            </div>
        </aside>

        <!-- Everything else is one card floating on the page background.
             On phones the card goes full-bleed: no gutter, no rounding — the
             floating-card idiom is a desktop trick that only wastes edge
             pixels on a 390px screen. -->
        <div class="min-w-0 flex-1 lg:p-2 lg:pl-0">
            <!-- overflow-hidden only where the rounded corners need clipping:
                 on phones it would kill every position:sticky inside. -->
            <div
                class="min-h-dvh bg-card lg:min-h-[calc(100dvh-1rem)] lg:overflow-hidden lg:rounded-2xl lg:border lg:border-border lg:shadow-(--shadow-card)"
            >
                <header
                    class="flex h-14 items-center gap-1 border-b border-border px-2 sm:gap-4 sm:px-4 lg:px-6"
                >
                    <button
                        type="button"
                        class="hidden size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground lg:grid"
                        aria-label="Mostrar u ocultar la barra lateral"
                        @click="sidebarExpanded = !sidebarExpanded"
                    >
                        <PanelLeft class="size-4" />
                    </button>
                    <span class="hidden h-5 w-px bg-border lg:block" aria-hidden="true" />

                    <!-- With no sidebar on phones, the app needs a name
                         somewhere: the mark anchors the header's left edge. -->
                    <RouterLink
                        to="/"
                        class="flex shrink-0 items-center gap-2 pr-1 lg:hidden"
                        aria-label="Quiniela"
                    >
                        <span
                            class="grid size-7 shrink-0 place-items-center rounded-[7px] bg-foreground text-[11px] font-bold text-background"
                        >
                            Q
                        </span>
                        <span class="hidden text-sm font-semibold tracking-[-0.02em] sm:inline">
                            Quiniela
                        </span>
                    </RouterLink>

                    <form
                        class="relative min-w-0 flex-1 sm:max-w-sm"
                        role="search"
                        @submit.prevent="submitSearch"
                    >
                        <Search
                            class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                            ref="searchInput"
                            v-model="query"
                            type="search"
                            @input="onSearchInput()"
                            placeholder="Buscar jugador..."
                            class="h-9 w-full rounded-md border border-input bg-card pl-9 pr-3 text-xs outline-none transition-shadow placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/25 sm:pl-10 sm:pr-12"
                        />
                        <kbd
                            class="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-secondary px-1.5 py-0.5 font-sans text-[11px] text-muted-foreground sm:block"
                        >
                            ⌘ K
                        </kbd>
                    </form>

                    <div class="ml-auto flex shrink-0 items-center gap-1">
                        <span class="hidden px-2 text-xs font-medium text-muted-foreground lg:inline">
                            {{ season }}
                        </span>
                        <!-- Admins lose the sidebar on phones; the panel stays
                             one tap away up here. -->
                        <RouterLink
                            v-if="isAdmin"
                            to="/admin"
                            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary lg:hidden"
                            aria-label="Panel de admin"
                        >
                            <Settings class="size-4" />
                        </RouterLink>
                        <button
                            type="button"
                            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary"
                            :aria-label="isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'"
                            @click="toggleTheme"
                        >
                            <Sun v-if="isDark" class="size-4" />
                            <Moon v-else class="size-4" />
                        </button>
                        <span class="mx-2 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
                        <RouterLink
                            to="/profile"
                            class="grid size-8 place-items-center rounded-full bg-secondary text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                            aria-label="Mi perfil"
                        >
                            {{ initials }}
                        </RouterLink>
                    </div>
                </header>

                <!-- @container: every page keys its grids on THIS column's
                     width, not the viewport — the sidebar no longer distorts
                     breakpoints, and collapsing it reflows the content.
                     pb-24 below lg clears the bottom bar. -->
                <main class="@container px-3 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-4 sm:px-4 lg:pb-4">
                    <RouterView />
                </main>
            </div>
        </div>

        <!-- The phone tab bar. Five destinations, thumb-height, safe-area
             aware, always visible — a vanishing tab bar disorients exactly
             the audience this app serves. Desktop keeps the sidebar and
             never sees this. -->
        <nav
            class="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] lg:hidden"
            aria-label="Navegación principal"
        >
            <div class="grid grid-cols-5">
                <RouterLink
                    v-for="item in gameNav"
                    :key="item.to"
                    v-slot="{ isActive, isExactActive, navigate, href }"
                    :to="item.to"
                    custom
                >
                    <a
                        :href="href"
                        class="flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium"
                        @click="navigate"
                    >
                        <!-- The pill, not just a tint: an active state that
                             lands for older eyes without leaving the zinc
                             register. -->
                        <span
                            :class="[
                                'grid h-6 w-11 place-items-center rounded-full transition-colors',
                                (item.to === '/' ? isExactActive : isActive)
                                    ? 'bg-secondary text-foreground'
                                    : 'text-muted-foreground'
                            ]"
                        >
                            <component
                                :is="item.icon"
                                class="size-5"
                                :stroke-width="(item.to === '/' ? isExactActive : isActive) ? 2.1 : 1.8"
                            />
                        </span>
                        <span
                            :class="
                                (item.to === '/' ? isExactActive : isActive)
                                    ? 'font-semibold text-foreground'
                                    : 'text-muted-foreground'
                            "
                        >
                            {{ item.short }}
                        </span>
                    </a>
                </RouterLink>
            </div>
        </nav>
    </div>
</template>
